import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Building, Users, BookOpen, ArrowRight, Globe, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimateIn } from '../components/Layout';
import { panIndiaRegions } from '../data/mock';

const industries = [
  { title: "Manufacturing", description: "Factory compliance, worker safety, industrial disputes, and production facility regulations.", icon: Building },
  { title: "IT & Technology", description: "Employee contracts, work-from-home policies, and IT sector-specific compliance.", icon: Globe },
  { title: "Healthcare", description: "Hospital staff regulations, shift management, and healthcare worker compliance.", icon: Users },
  { title: "Construction", description: "Building worker safety, migrant labour compliance, and site regulations.", icon: Building },
  { title: "Retail & FMCG", description: "Shop establishment compliance, multi-state operations, and workforce management.", icon: Users },
  { title: "Logistics", description: "Transportation worker regulations, inter-state compliance, and fleet workforce management.", icon: MapPin },
];

const trainingTopics = [
  "Labour law awareness for HR teams",
  "Compliance procedures & best practices",
  "New labour codes transition workshops",
  "Industry-specific compliance training",
  "Management briefings on regulatory changes",
];

const Expertise = () => {
  return (
    <div className="pt-20">
      {/* Page Hero */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.25em] uppercase mb-4 block">Expertise</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B1F3A] leading-tight mb-6">
              Deep Domain<br />Knowledge
            </h1>
            <p className="text-gray-500 font-body text-lg max-w-2xl leading-relaxed mb-6">
              Three decades of experience across industries, geographies, and the full spectrum of Indian labour law.
            </p>
            <div className="w-16 h-[2px] bg-[#C9A646]" />
          </AnimateIn>
        </div>
      </section>

      {/* Industry Experience */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <div className="mb-16">
              <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Industries</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3">Industry Experience</h2>
              <div className="w-16 h-[2px] bg-[#C9A646] mt-4" />
            </div>
          </AnimateIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, idx) => {
              const Icon = industry.icon;
              return (
                <AnimateIn key={idx} delay={idx * 100}>
                  <div className="bg-white p-8 rounded-sm hover:shadow-lg transition-shadow duration-300 group">
                    <div className="w-12 h-12 rounded-full bg-[#C9A646]/10 flex items-center justify-center mb-5 group-hover:bg-[#C9A646]/20 transition-colors duration-300">
                      <Icon size={22} className="text-[#C9A646]" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mb-3">{industry.title}</h3>
                    <p className="text-gray-500 font-body text-sm leading-relaxed">{industry.description}</p>
                  </div>
                </AnimateIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* PAN India Coverage */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <div className="text-center mb-16">
              <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Coverage</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3">PAN India Presence</h2>
              <p className="text-gray-500 font-body text-lg mt-4 max-w-2xl mx-auto">
                Comprehensive labour law compliance expertise across all major Indian states and union territories.
              </p>
              <div className="w-16 h-[2px] bg-[#C9A646] mx-auto mt-4" />
            </div>
          </AnimateIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {panIndiaRegions.map((region, idx) => (
              <AnimateIn key={idx} delay={idx * 100}>
                <div className="bg-[#F8F9FA] p-6 rounded-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin size={18} className="text-[#C9A646]" />
                    <h3 className="font-heading text-lg font-bold text-[#0B1F3A]">{region.zone}</h3>
                  </div>
                  <ul className="space-y-2">
                    {region.states.map((state, sIdx) => (
                      <li key={sIdx} className="flex items-center gap-2 text-gray-600 font-body text-sm">
                        <CheckCircle size={14} className="text-[#C9A646] shrink-0" />
                        {state}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* Training & Workshops */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <AnimateIn className="flex-1">
              <div>
                <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Training</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3 mb-6">Training & Workshops</h2>
                <p className="text-gray-500 font-body text-lg leading-relaxed mb-8">
                  Recognized trainer for industry organizations with a proven track record of building compliance capabilities across teams and organizations.
                </p>
                <ul className="space-y-4">
                  {trainingTopics.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-[#0B1F3A] font-body">
                      <CheckCircle size={18} className="text-[#C9A646] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </AnimateIn>
            <AnimateIn delay={200} className="flex-1 w-full">
              <div className="bg-[#0B1F3A] p-10 rounded-sm">
                <BookOpen size={40} className="text-[#C9A646] mb-6" />
                <h3 className="font-heading text-2xl font-bold text-white mb-4">Custom Training Programs</h3>
                <p className="text-gray-400 font-body leading-relaxed mb-6">
                  Tailored training modules designed for your specific industry, workforce size, and compliance requirements. Available in both classroom and online formats.
                </p>
                <Link to="/consultation">
                  <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-6 h-10">
                    Request Training <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </AnimateIn>
          </div>
        </div>
      </section>

      {/* Case Studies Placeholder */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimateIn>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Coming Soon</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3 mb-6">Case Studies</h2>
            <p className="text-gray-500 font-body text-lg max-w-2xl mx-auto mb-8">
              Detailed case studies showcasing successful compliance transformations across industries are being prepared.
            </p>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white font-body rounded-sm px-8 h-11">
                Get In Touch for References
              </Button>
            </Link>
          </AnimateIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B1F3A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimateIn>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">Leverage My Expertise</h2>
            <p className="text-gray-400 font-body text-lg max-w-2xl mx-auto mb-10">
              Let my three decades of experience work for your organization's compliance needs.
            </p>
            <Link to="/consultation">
              <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12 text-base">
                Book Consultation
              </Button>
            </Link>
          </AnimateIn>
        </div>
      </section>
    </div>
  );
};

export default Expertise;
